import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, useRouteError } from "react-router-dom";
import { RootLayout } from "./layouts/RootLayout";
import { LandingPage } from "./routes/LandingPage";
import { NotFoundPage } from "./routes/NotFoundPage";

// When a lazy-loaded route chunk fails to load — almost always a stale
// browser cache referencing a previous build's hashed asset filename —
// React Router routes the thrown error here. Real 404s (an unknown URL)
// also land here, but those don't match the chunk-error fingerprint so
// they keep falling through to <NotFoundPage />.
//
// The fix: auto-reload the page. The Go server serves index.html with
// `Cache-Control: no-cache, no-store, must-revalidate`, so the reload
// fetches the new shell with the current chunk hashes and everything
// just works. Throttled via sessionStorage so a genuinely missing
// chunk doesn't trap the user in an infinite reload loop.
function ChunkErrorBoundary() {
  const error = useRouteError();
  useEffect(() => {
    const msg = String((error as { message?: string })?.message ?? error ?? "");
    const isChunkErr =
      /chunk|dynamically imported module|failed to fetch/i.test(msg) ||
      (error as { name?: string })?.name === "ChunkLoadError";
    if (!isChunkErr) return;
    const RELOAD_THROTTLE_MS = 30_000;
    const last = Number(sessionStorage.getItem("chunkReloadedAt") || 0);
    if (Date.now() - last < RELOAD_THROTTLE_MS) return;
    sessionStorage.setItem("chunkReloadedAt", String(Date.now()));
    window.location.reload();
  }, [error]);
  return <NotFoundPage />;
}

// Landing is the entry route — keep it eager so first paint isn't gated on a
// chunk fetch. Everything else is lazy: gallery, detail, dashboard, editor,
// and especially GuestPage (which pulls in all 29 variants and is only
// visited from a shared invitation link).
const GalleryPage = lazy(() =>
  import("./routes/GalleryPage").then((m) => ({ default: m.GalleryPage })),
);
const TemplateDetailPage = lazy(() =>
  import("./routes/TemplateDetailPage").then((m) => ({ default: m.TemplateDetailPage })),
);
const CheckoutPage = lazy(() =>
  import("./routes/CheckoutPage").then((m) => ({ default: m.CheckoutPage })),
);
const PaymentReturnPage = lazy(() =>
  import("./routes/PaymentReturnPage").then((m) => ({ default: m.PaymentReturnPage })),
);
const DashboardPage = lazy(() =>
  import("./routes/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const GuestPage = lazy(() =>
  import("./routes/GuestPage").then((m) => ({ default: m.GuestPage })),
);
const LoginPage = lazy(() =>
  import("./routes/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const TelegramCallbackPage = lazy(() =>
  import("./routes/TelegramCallbackPage").then((m) => ({ default: m.TelegramCallbackPage })),
);
const InvitationEditorPage = lazy(() =>
  import("./routes/InvitationEditorPage").then((m) => ({
    default: m.InvitationEditorPage,
  })),
);
const OwnerMenuPage = lazy(() =>
  import("./routes/OwnerMenuPage").then((m) => ({ default: m.OwnerMenuPage })),
);
const InvitationWishesPage = lazy(() =>
  import("./routes/InvitationWishesPage").then((m) => ({ default: m.InvitationWishesPage })),
);
const InvitationUrlPage = lazy(() =>
  import("./routes/InvitationUrlPage").then((m) => ({ default: m.InvitationUrlPage })),
);
const SharePage = lazy(() =>
  import("./routes/SharePage").then((m) => ({ default: m.SharePage })),
);
const HelpPage = lazy(() =>
  import("./routes/HelpPage").then((m) => ({ default: m.HelpPage })),
);
const PrivacyPage = lazy(() =>
  import("./routes/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("./routes/TermsPage").then((m) => ({ default: m.TermsPage })),
);

// Tiny route fallback — the route chunks are small enough that this is
// rarely visible, but a single hairline gives the page some weight while
// the JS arrives instead of an obvious blank flash.
const RouteFallback = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "grid",
      placeItems: "center",
      color: "var(--ink-3, #6b6864)",
      fontFamily: "var(--font-ui)",
      fontSize: 12,
      letterSpacing: "0.32em",
    }}
    role="status"
    aria-live="polite"
  >
    <span style={{ opacity: 0.7 }}>YUKLANMOQDA · LOADING</span>
  </div>
);

const lazyRoute = (Component: React.ComponentType) => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

// Real URLs (no #/...). The Go server serves index.html for any
// non-/api/* path so deep links work on hard refresh.
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ChunkErrorBoundary />,
    children: [
      { index: true, element: <LandingPage /> },
      // English URLs are the canonical, stable surface; the Uzbek-named
      // paths (shablonlar / kirish / yordam) stay registered as aliases
      // so previously-shared links and bookmarks keep working forever.
      // Internal navigation (NavLink, programmatic redirects, sitemap)
      // points at the English routes going forward.
      { path: "templates", element: lazyRoute(GalleryPage) },
      { path: "templates/:id", element: lazyRoute(TemplateDetailPage) },
      { path: "shablonlar", element: lazyRoute(GalleryPage) },
      { path: "shablonlar/:id", element: lazyRoute(TemplateDetailPage) },
      // Per-ceremony SEO landing pages — clean, keyword-exact Uzbek slugs
      // that the Go backend server-renders for crawlers (see seoCategoryPage
      // in seo_marketing.go). For real users the SPA renders the same gallery
      // pre-filtered to that category. taklifla.uz only; keep this list in
      // sync with seoCategoryPages on the backend.
      { path: "toy-taklifnomasi", element: (<Suspense fallback={<RouteFallback />}><GalleryPage initialCategory="wedding" /></Suspense>) },
      { path: "nikoh-taklifnomasi", element: (<Suspense fallback={<RouteFallback />}><GalleryPage initialCategory="nikoh" /></Suspense>) },
      { path: "tugilgan-kun-taklifnomasi", element: (<Suspense fallback={<RouteFallback />}><GalleryPage initialCategory="birthday" /></Suspense>) },
      { path: "korporativ-taklifnoma", element: (<Suspense fallback={<RouteFallback />}><GalleryPage initialCategory="corporate" /></Suspense>) },
      { path: "sevgi-taklifnomasi", element: (<Suspense fallback={<RouteFallback />}><GalleryPage initialCategory="sevgi" /></Suspense>) },
      { path: "checkout/:id", element: lazyRoute(CheckoutPage) },
      // Click and Payme both redirect users to /checkout/:id/return after
      // payment. We pass `?order=<id>&provider=<click|payme>` as query
      // params; the page polls /api/v1/payments/orders/:id until the
      // gateway's webhook flips the order out of `pending`.
      { path: "checkout/:id/return", element: lazyRoute(PaymentReturnPage) },
      // Convenience alias — both gateways accept any URL we register, but
      // this lets us hand them a single fixed return URL that doesn't
      // depend on knowing the invitation slug at config time.
      { path: "checkout/return", element: lazyRoute(PaymentReturnPage) },
      { path: "dashboard", element: lazyRoute(DashboardPage) },
      {
        path: "dashboard/invitations/new",
        element: (
          <Suspense fallback={<RouteFallback />}>
            <InvitationEditorPage mode="create" />
          </Suspense>
        ),
      },
      // Owner menu — the four-card choices screen (Tahrirlash / Tilaklar
      // / Link / Ulashish). Hubs out to focused single-purpose pages so
      // each action stays uncluttered.
      { path: "dashboard/invitations/:id", element: lazyRoute(OwnerMenuPage) },
      {
        path: "dashboard/invitations/:id/edit",
        element: (
          <Suspense fallback={<RouteFallback />}>
            <InvitationEditorPage mode="edit" />
          </Suspense>
        ),
      },
      { path: "dashboard/invitations/:id/wishes", element: lazyRoute(InvitationWishesPage) },
      { path: "dashboard/invitations/:id/url",    element: lazyRoute(InvitationUrlPage) },
      { path: "dashboard/invitations/:id/share",  element: lazyRoute(SharePage) },
      { path: "login", element: lazyRoute(LoginPage) },
      { path: "kirish", element: lazyRoute(LoginPage) },
      // Telegram OIDC callback — registered as the redirect_uri on
      // BotFather. The page reads ?code=…&state=… from the URL, pulls
      // the stashed PKCE verifier out of sessionStorage, and POSTs the
      // exchange request to the backend.
      { path: "auth/telegram/callback", element: lazyRoute(TelegramCallbackPage) },
      { path: "help", element: lazyRoute(HelpPage) },
      { path: "yordam", element: lazyRoute(HelpPage) },
      { path: "privacy", element: lazyRoute(PrivacyPage) },
      { path: "terms", element: lazyRoute(TermsPage) },
    ],
  },
  // Public guest invitation — bare layout, no nav/footer chrome.
  // New shape: /<token>/<name-slug>  e.g. /xk2j9/aziza-and-sardor — that's
  // what newly-created invitations expose. Legacy /i/<slug> stays for
  // bookmarks and demos. Both routes render the same GuestPage; ordering
  // matters because react-router would otherwise let `/<token>/...` swallow
  // routes like `/dashboard`, but those are matched ahead of the catch-all.
  { path: "/i/:slug", element: lazyRoute(GuestPage), errorElement: <ChunkErrorBoundary /> },
  { path: "/:token/:nameSlug", element: lazyRoute(GuestPage), errorElement: <ChunkErrorBoundary /> },
]);
